import api from "@/lib/apiClient";
import i18n from "@/i18n"; // HEB-1: attach the interface language to Mira requests
import { runtimeConfig } from "@/lib/runtimeConfig"; // MIRA-2: base URL for the streaming fetch
import { getPreviewTargetUserId } from "@/lib/previewMode"; // MIRA-2: mirror the axios preview header

import { assessmentApi, adherenceApi } from "@/features/assessments/api";
import {
  exemplarApi,
  recognitionApi,
  shareAssetApi,
} from "@/features/recognition/api";
import {
  curriculumApi,
  frameworkApi,
  lessonPlanApi,
  recordingComplianceApi,
  recordingPolicyApi,
  schoolApi,
  syllabusApi,
} from "@/features/school-setup/api";
import {
  actionPlanApi,
  adminCoachingApi,
  coachNoteApi,
  lessonThreadApi,
  observationApi,
  observerGoalApi,
  privacyProfileApi,
  recognitionWallApi,
  teacherApi,
} from "@/features/teachers/api";
import { evidenceApi, videoApi } from "@/features/videos/api";

export const authApi = {
  login: (payload) => api.post("/api/auth/login", payload),
  logout: () => api.post("/api/auth/logout"),
  register: (payload) => api.post("/api/auth/register", payload),
  requestAccess: (payload) => api.post("/api/auth/request-access", payload),
  institutionLookup: (params) => api.get("/api/institutions/lookup", { params }),
  requestPasswordReset: (payload) => api.post("/api/auth/password-reset/request", payload),
  confirmPasswordReset: (payload) => api.post("/api/auth/password-reset/confirm", payload),
  activateInvite: (payload) => api.post("/api/auth/invite/activate", payload), // PR-3a — set password to activate
  me: () => api.get("/api/me"),
  getWorkspaceMode: () => api.get("/api/user/workspace-mode"),
  setWorkspaceMode: (payload) => api.post("/api/user/workspace-mode", payload),
  // PRIV-2 (MFA) — voluntary enrollment (authenticated), plus the token-authorised login MFA steps.
  mfaEnroll: () => api.post("/api/auth/mfa/enroll"),
  mfaActivate: (payload) => api.post("/api/auth/mfa/activate", payload),
  mfaChallenge: (payload) => api.post("/api/auth/mfa/challenge", payload),
  mfaForcedEnrollStart: (payload) => api.post("/api/auth/mfa/enroll-required/start", payload),
  mfaForcedEnrollComplete: (payload) => api.post("/api/auth/mfa/enroll-required/complete", payload),
  // priv-2-fix — the voluntary security-settings surface.
  mfaStatus: () => api.get("/api/auth/mfa/status"),
  mfaDisable: (payload) => api.post("/api/auth/mfa/disable", payload),
  mfaRecoveryRegenerate: (payload) => api.post("/api/auth/mfa/recovery/regenerate", payload),
};

export { assessmentApi, adherenceApi };
export { exemplarApi, recognitionApi, shareAssetApi };
export {
  curriculumApi,
  frameworkApi,
  lessonPlanApi,
  recordingComplianceApi,
  recordingPolicyApi,
  schoolApi,
  syllabusApi,
};
export {
  actionPlanApi,
  adminCoachingApi,
  coachNoteApi,
  lessonThreadApi,
  observationApi,
  observerGoalApi,
  privacyProfileApi,
  recognitionWallApi,
  teacherApi,
};
export { evidenceApi, videoApi };

export const notificationApi = {
  list: () => api.get("/api/notifications"),
  unreadCount: () => api.get("/api/notifications/unread-count"),
  markAllRead: () => api.post("/api/notifications/read-all"),
  // W30 — the lesson-thread unread marker reads the unread rows and clears one lesson's at a time.
  // W30 — `limit: 100` is the server's own ceiling (`min(int(limit or 20), 100)`), so this asks for
  // the maximum and cannot ask for more. `page` is the only way past it, and the endpoint takes no
  // source filter, so the caller pages. See useLessonThreadUnread for why it has to.
  unread: (page = 1) =>
    api.get("/api/notifications", { params: { unread_only: true, limit: 100, page } }),
  markRead: (notificationId) => api.post(`/api/notifications/${notificationId}/read`),
};

export const privacyReviewApi = {
  queue: () => api.get("/api/privacy/review-queue"),
  resolve: (videoId, payload) => api.post(`/api/videos/${videoId}/privacy/review`, payload),
};

export const scheduleApi = {
  list: (params) => api.get("/api/schedules", { params }),
  create: (payload) => api.post("/api/schedules", payload),
  update: (id, payload) => api.patch(`/api/schedules/${id}`, payload),
};

export const reportApi = {
  history: () => api.get("/api/reports/history"),
  coachingSnapshot: () => api.get("/api/reports/coaching-snapshot"),
  cohortSnapshot: () => api.get("/api/reports/cohort-snapshot"),
  exportCoachingSnapshotCsv: () =>
    api.get("/api/reports/export/coaching-snapshot.csv", { responseType: "blob" }),
  exportCohortSnapshotCsv: () =>
    api.get("/api/reports/export/cohort-snapshot.csv", { responseType: "blob" }),
  export: (format, params = {}) =>
    api.post(
      "/api/reports/export",
      new URLSearchParams({ format, ...params }),
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        responseType: "blob",
      }
    ),
};

// B8-a.5 — department registry (org-doc controlled vocabulary). Admin-only endpoints.
export const departmentsApi = {
  list: () => api.get("/api/admin/departments"),
  add: (name) => api.post("/api/admin/departments", { name }),
  remove: (name) => api.delete(`/api/admin/departments/${encodeURIComponent(name)}`),
};

// W62 — the org's academic structure (app/routers/academic_structure.py). Reads are open to any
// member of the caller's own org (teacher or admin) and fenced to it server-side.
export const academicApi = {
  listGrades: () => api.get("/api/academic/grades"),
};

// R4 — Mira (Reflective Space) surface. All endpoints 404 when REFLECTIVE_SPACE_ENABLED
// is off; the launcher treats a failed opening() as "off" and renders nothing.
// MIRA-2: parse one SSE frame ("event: <name>\ndata: <json>") into {event, data}. Tolerant of
// multi-line data and missing event lines (defaults to "message").
export function parseSseFrame(frame) {
  let event = "message";
  const dataLines = [];
  for (const raw of frame.split("\n")) {
    const line = raw.replace(/\r$/, "");
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return null;
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) };
  } catch {
    return null;
  }
}

// MIRA-2: stream a Mira turn as Server-Sent Events over a POST fetch (ReadableStream). We use
// native fetch (not the axios client) because axios can't expose an incremental response body in
// the browser; the auth Bearer token + preview header are attached here to mirror the axios
// interceptor. Callbacks: onStart(message_ref) fires FIRST (so thumbs can bind before any text),
// onDelta(text) per chunk, onDone(reply, meta) at completion, onError({fallback_cause}) on failure.
async function respondStream(payload, { onStart, onDelta, onDone, onError, signal } = {}) {
  const baseUrl = runtimeConfig.backendUrl || "";
  const token = (typeof localStorage !== "undefined" && localStorage.getItem("cognivio_token")) || "";
  const previewUserId = getPreviewTargetUserId();
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (previewUserId) headers["X-Cognivio-Preview-User"] = previewUserId;

  let resp;
  try {
    resp = await fetch(`${baseUrl}/api/reflective/respond/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify({ ...payload, language: i18n.language }),
      signal,
    });
  } catch (err) {
    if (onError) onError({ fallback_cause: "network_error", error: err });
    return;
  }
  if (!resp.ok || !resp.body || !resp.body.getReader) {
    if (onError) onError({ fallback_cause: "http_error", status: resp.status });
    return;
  }

  // ⚑ C-385 §4 — WHETHER A TERMINAL EVENT EVER ARRIVED, tracked on the EVENT and not on the
  // callback, so a caller that passes no `onDone` still counts a `done` as terminal.
  let sawTerminal = false;

  const dispatch = (evt) => {
    if (!evt) return;
    if (evt.event === "start" && onStart) onStart(evt.data.message_ref);
    else if (evt.event === "delta" && onDelta) onDelta(evt.data.text || "");
    else if (evt.event === "done") {
      sawTerminal = true;
      if (onDone) onDone(evt.data.reply || {}, evt.data);
    } else if (evt.event === "error") {
      sawTerminal = true;
      if (onError) onError(evt.data || { fallback_cause: "stream_error" });
    }
  };

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        dispatch(parseSseFrame(buffer.slice(0, idx)));
        buffer = buffer.slice(idx + 2);
      }
    }
    if (buffer.trim()) dispatch(parseSseFrame(buffer));
    // ⚑ C-385 §4 — A STREAM THAT SUCCEEDS AND SAYS NOTHING IS A FAILURE, AND IT WAS THE ONE MODE
    // F-10 MISSED. This is my own gap, found while checking a hypothesis the new prod evidence had
    // just killed.
    //
    // F-10 handled four modes — `network_error`, `http_error`, a mid-stream `error` frame, and a
    // read that throws — and every one of them ends in a callback. **A 200 whose body yields no
    // parseable frame ends in none at all.** The loop reads to `done` dispatching nothing, the
    // trailing buffer is empty, nothing throws, and this function RETURNS SILENTLY: no `onStart`,
    // no `onDelta`, no `onDone`, no `onError`.
    //
    // ⚑ What the reader then sees is the worst of the available outcomes: `MiraLauncher`'s
    // `.finally` clears the composer, but the message was never patched, so the bubble stays
    // `{text: "", streaming: true}` — an empty reply with a blinking cursor, no error, and no retry.
    // Forever. A failure that announces itself is strictly better than one that looks like Mira
    // thinking.
    //
    // C-385 §4 reports exactly this shape from prod: `200`, `transferSize 0`, `5.85 s`. `empty_stream`
    // is its own cause so the console line distinguishes it from the four that were already named —
    // which is the whole point of F-10 and the reason not to fold it into `stream_error`.
    if (!sawTerminal && onError) onError({ fallback_cause: "empty_stream" });
  } catch (err) {
    if (err && err.name === "AbortError") return; // user stopped generation — not an error
    if (onError) onError({ fallback_cause: "stream_error", error: err });
  }
}

export const reflectiveApi = {
  opening: () => api.get("/api/reflective/opening"),
  respond: (payload) => api.post("/api/reflective/respond", { ...payload, language: i18n.language }),
  // MIRA-2: the streaming twin — reply text arrives incrementally (see respondStream above).
  respondStream,
  beliefs: () => api.get("/api/reflective/beliefs"),
  correction: (payload) => api.post("/api/reflective/correction", payload),
  // PLAN-2 M3 — the teacher-private "review my plan" intent (structured result).
  planReview: (planId) => api.post("/api/reflective/plan-review", { plan_id: planId, language: i18n.language }),
  // PLAN-2 M5 — the admin pre-conference briefing on one teacher (own view only).
  preConferenceBrief: (teacherId) =>
    api.post("/api/reflective/pre-conference-brief", { teacher_id: teacherId }),
  // P7b — execute a USER-CONFIRMED action proposal. The backend re-validates authority and runs
  // it AS the user. The LLM never executes; this fires only on the user's Confirm click.
  executeAction: (payload) => api.post("/api/reflective/actions/execute", payload),
  // SIG-2: signal-only endpoints (fire-and-forget from the FE — never block the UI on them).
  rejectAction: (payload) => api.post("/api/reflective/actions/reject", payload),
  rateMessage: (payload) => api.post("/api/reflective/messages/rating", payload),
  // Cluster B — the document view: read the user's OWN Mira-created documents.
  documents: () => api.get("/api/reflective/documents"),
  document: (id) => api.get(`/api/reflective/documents/${id}`),
  // Cluster C — soft-delete + restore (the 7s-undo pair). Owner-scoped.
  deleteDocument: (id) => api.delete(`/api/reflective/documents/${id}`),
  restoreDocument: (id) => api.post(`/api/reflective/documents/${id}/restore`),
  // MIRA-2 Unit L — edit a document in place (full title+body replace). Owner-scoped (same guard).
  editDocument: (id, payload) => api.patch(`/api/reflective/documents/${id}`, payload),
  // W55 — her saved conversations. Owner-fenced server-side; the list carries no turns.
  conversations: () => api.get("/api/reflective/conversations"),
  conversation: (id) => api.get(`/api/reflective/conversations/${encodeURIComponent(id)}`),
  saveConversation: (payload) => api.post("/api/reflective/conversations", payload),
  deleteConversation: (id) => api.delete(`/api/reflective/conversations/${encodeURIComponent(id)}`),
  // P7b-Tier 1 — proactive suggestions (the badge + on-open list) and per-user snooze/dismiss.
  suggestions: () => api.get("/api/reflective/suggestions"),
  suggestionsCount: () => api.get("/api/reflective/suggestions/count"),
  snoozeSuggestion: (key, until) => api.post(`/api/reflective/suggestions/${encodeURIComponent(key)}/snooze`, { until }),
  dismissSuggestion: (key) => api.post(`/api/reflective/suggestions/${encodeURIComponent(key)}/dismiss`),
};

// S-2 — ephemeral staff chat (in-org, polling). All endpoints auth-required + org-scoped
// server-side. The conversation thread is the POLLED endpoint (FE refetchInterval ~2-3s).
export const chatApi = {
  contacts: () => api.get("/api/chat/contacts"),
  conversations: () => api.get("/api/chat/conversations"),
  createConversation: (payload) => api.post("/api/chat/conversations", payload),
  messages: (conversationId) => api.get(`/api/chat/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, text) =>
    api.post(`/api/chat/conversations/${conversationId}/messages`, { text }),
};

// Calendar-A — native org calendar (single events + RSVP + per-viewer timezone). Auth-required
// + org-scoped server-side. GET events is window-bounded (agenda/week ask for a range) and polled.
export const calendarApi = {
  events: (params) => api.get("/api/calendar/events", { params }),
  createEvent: (payload) => api.post("/api/calendar/events", payload),
  updateEvent: (id, payload) => api.patch(`/api/calendar/events/${id}`, payload),
  deleteEvent: (id, params) => api.delete(`/api/calendar/events/${id}`, params ? { params } : undefined),
  rsvp: (id, rsvp) => api.post(`/api/calendar/events/${id}/rsvp`, { rsvp }),
};

// Calendar-A — the caller's own per-user settings (the timezone control's backend).
export const userSettingsApi = {
  update: (payload) => api.patch("/api/me/settings", payload),
};

// ADMIN-A2 — the principal's Home data (consent-clean counts + the school's week + trend + note).
export const adminHomeApi = {
  get: () => api.get("/api/admin/home"),
  // W32 (D3): the three engagement measures for this week / this month, k = 3 server-side.
  engagement: (period) => api.get("/api/admin/home/engagement", { params: { period } }),
};

// ADMIN-A3 — My Team roster: one row per org teacher (sessions, shares, stars). Read-only, org-scoped.
export const adminTeamApi = {
  get: () => api.get("/api/admin/team"),
  // ADMIN-A5a — the principal drills into ONE teacher's shared lessons (D2-scoped, read-only).
  teacherLessons: (teacherId) => api.get(`/api/admin/teachers/${teacherId}/lessons`),
  // ADMIN-A5b — the growth drill: shared-set-only recompute, viewer door closed (words, not windows).
  teacherGrowth: (teacherId) => api.get(`/api/admin/teachers/${teacherId}/growth`),
  // ADMIN-A6a — the Cognivio session workflow (admin seat).
  requestSession: (teacherId, payload) => api.post(`/api/admin/teachers/${teacherId}/cognivio-sessions`, payload),
  completeSession: (teacherId, sessionId) =>
    api.patch(`/api/admin/teachers/${teacherId}/cognivio-sessions/${sessionId}`, { status: "completed" }),
  remind: (teacherId) => api.post(`/api/admin/teachers/${teacherId}/remind`),
  // ADMIN-A6b — set/edit/clear the prep note on a requested/scheduled session (edit = re-scan server-side).
  setPrepNote: (teacherId, sessionId, prepNote) =>
    api.put(`/api/admin/teachers/${teacherId}/cognivio-sessions/${sessionId}/prep-note`, { prep_note: prepNote }),
};

// ADMIN-A4 — School Progress: k-guarded org rollups in the teacher growth vocabulary, filtered by subject.
export const adminProgressApi = {
  get: (department) => api.get("/api/admin/school-progress", department ? { params: { department } } : undefined),
};

// ADMIN-D1 (A9) — School Activity aggregates: three org-scoped totals + prior-period deltas over the
// shared allow-set. When aggregate_dashboards_enabled is off the counter keys are ABSENT (not zeroed) —
// the strip is a designed absence. range = "month" | "term".
export const schoolActivityApi = {
  get: (range) => api.get("/api/admin/school-activity", range ? { params: { range } } : undefined),
};

// ADMIN-D2 — School Growth: per-focus-area TRENDS as words (never scores) + the monthly volume
// series. When aggregate_dashboards_enabled is off the aggregate keys are ABSENT (not zeroed) — the
// page section is a designed absence. range = "term" | "year" (a month of growth is noise).
export const schoolGrowthApi = {
  // W35: `department` narrows the page to that department's teachers (the server folds case and
  // withholds every number that would rest on fewer than three of them). Omitted when empty.
  get: (range, department) => {
    const params = {};
    if (range) params.range = range;
    if (department) params.department = department;
    return api.get("/api/admin/school-growth", Object.keys(params).length ? { params } : undefined);
  },
};

// ADMIN-A4.5 — the two quiet places. Library = the org's shared lessons (read-only); Setup = framework + areas.
export const adminLibraryApi = {
  get: () => api.get("/api/admin/library"),
};
export const adminSetupApi = {
  get: () => api.get("/api/admin/school-setup"),
  // UPLOAD-CAP-1 — READ ONLY. There is deliberately no setter here: only a master admin can change
  // a cap, on a different router, with its own guard and audit trail.
  uploadUsage: () => api.get("/api/admin/upload-usage"),
};

// ADMIN-A7 — Rubric Builder authoring (school_admin only). rename / reweight / archive
// the org's focus areas. Weights are a DESCRIPTIVE attention + order share, never a score.
export const rubricBuilderApi = {
  get: () => api.get("/api/admin/rubric/builder"),
  patchArea: (areaId, body) => api.patch(`/api/admin/rubric/areas/${encodeURIComponent(areaId)}`, body),
  createArea: (body) => api.post("/api/admin/rubric/areas", body),
};

// Per-user notification preferences (GET/PATCH). Thin seams over the existing endpoints — pages
// route through here instead of raw api.get/patch so every call site shares one contract.
export const notificationPreferencesApi = {
  get: () => api.get("/api/user/notification-preferences"),
  update: (payload) => api.patch("/api/user/notification-preferences", payload),
};

// REMIND-1B — Web Push subscriptions (owner-scoped, per-device). publicKey returns the
// non-secret VAPID key + whether push is configured server-side.
export const pushApi = {
  publicKey: () => api.get("/api/user/push-subscriptions/public-key"),
  list: () => api.get("/api/user/push-subscriptions"),
  subscribe: (payload) => api.post("/api/user/push-subscriptions", payload),
  unsubscribe: (endpoint) => api.delete("/api/user/push-subscriptions", { data: { endpoint } }),
};

// Training-admin cohorts.
export const cohortApi = {
  list: () => api.get("/api/cohorts"),
  create: (payload) => api.post("/api/cohorts", payload), // ADMIN-COHORTS: create + name from the UI
  rename: (cohortId, payload) => api.patch(`/api/cohorts/${cohortId}`, payload), // ADMIN-COHORTS: rename
};

// Observation session creation (the observation-setup flow).
export const observationSessionApi = {
  create: (payload) => api.post("/api/observation-sessions", payload),
};

export const adminWorkspaceApi = {
  dashboard: (params) => api.get("/api/admin/workspace/dashboard", { params }),
  search: (params) => api.get("/api/admin/workspace/search", { params }),
  // B8-c Panel 2 — faceted all-teachers observation list over the shared roster core.
  observationList: (params) => api.get("/api/admin/teachers/observation-list", { params }),
  // B8-e Panel 4 — grading-provenance trace, drilled from Panel 2:
  //   teacher -> that teacher's assessments -> one assessment's full derivation chain.
  teacherAssessments: (teacherId) =>
    api.get(`/api/admin/teachers/${teacherId}/assessments`),
  assessmentProvenance: (teacherId, assessmentId) =>
    api.get(`/api/admin/teachers/${teacherId}/assessments/${assessmentId}/provenance`),
  // Demo Phase 2 rule engine (wired in 4b-R3b): per-teacher exclude-from-counts. The
  // endpoints already exist; these are the thin client seams.
  coachingExclusions: () => api.get("/api/admin/coaching-exclusions"),
  excludeTeacher: (teacherId) => api.post(`/api/admin/coaching-exclusions/teachers/${teacherId}`),
  includeTeacher: (teacherId) => api.delete(`/api/admin/coaching-exclusions/teachers/${teacherId}`),
};

export const teacherWorkspaceApi = {
  latestLesson: () => api.get("/api/teachers/me/latest-lesson"),
  coachingTasks: () => api.get("/api/coaching/tasks"),
  taskReflection: (taskId, payload) =>
    api.post(`/api/coaching/tasks/${taskId}/reflection`, payload),
  reflections: () => api.get("/api/coaching/reflections/my"),
  recognition: () => api.get("/api/recognition/my-badges"),
};

export const trainingApi = {
  supervisorSummary: () => api.get("/api/training/supervisor-summary"),
};

export const demoApi = {
  reset: (persona) => api.post("/api/demo/reset", null, { params: { persona } }),
  seed: (payload) => api.post("/api/demo/seed", payload),
};

export const gradebookApi = {
  list: () => api.get("/api/integrations/gradebook"),
  connect: (payload) => api.post("/api/integrations/gradebook", payload),
};

export const adminApi = {
  // W10 (G3 handback §3): the admin's observation sessions for an arbitrary week [start, end).
  sessions: ({ start, end }) => api.get("/api/admin/sessions", { params: { start, end } }),
  setScoringMode: (scoring_mode) =>
    api.post("/api/admin/preferences/scoring-mode", { scoring_mode }),
  feedbackDigest: () => api.get("/api/admin/feedback-digest"),
  organizationMemory: (params) => api.get("/api/admin/organization-memory", { params }),
  accessUsers: () => api.get("/api/admin/access-users"),
  approveAccessUser: (userId, payload = {}) =>
    api.post(`/api/admin/access-users/${userId}/approve`, payload),
  revokeAccessUser: (userId, payload = {}) =>
    api.post(`/api/admin/access-users/${userId}/revoke`, payload),
};

// ADMIN-D4a — SESSION LINK. The teacher associates her recording with the Cognivio session it
// belongs to; the association shares it with her school. Owner-only on both sides, explicit-only.
export const sessionLinkApi = {
  candidates: (videoId) => api.get(`/api/teachers/me/videos/${videoId}/session-candidates`),
  link: (videoId, sessionId) =>
    api.post(`/api/teachers/me/videos/${videoId}/session-link`, { session_id: sessionId }),
};

export const onboardingApi = {
  status: () => api.get("/api/onboarding/status"),
  complete: (payload = {}) => api.post("/api/onboarding/complete", payload),
};

export const consentApi = {
  status: () => api.get("/api/consent/status"),
  grant: (payload) => api.post("/api/consent/grant", payload),
  withdraw: (payload) => api.post("/api/consent/withdraw", payload),
  records: () => api.get("/api/consent/records"),
  dataExport: () => api.get("/api/user/data-export", { responseType: "blob" }),
  erase: () => api.post("/api/user/right-to-erasure"),
};

/**
 * CONSENT-LEDGER-1 — the per-device camera consent LEDGER. Deliberately a separate client from
 * `consentApi` above, which is the older platform consent gate on the SINGULAR `/api/consent/*`
 * routes and is load-bearing for ProtectedRoute's redirect.
 *
 * The two families differ by one letter — `/api/consent/status` is the gate, and
 * `/api/consents/me/status` is this ledger — so putting a second `status` on `consentApi` would
 * leave two methods a plural apart in one object, and the wrong one is always reachable. Separate
 * names make the wrong call hard to write instead of easy.
 */
export const consentLedgerApi = {
  postEvent: (payload) => api.post("/api/consents/events", payload),
  status: (deviceId) =>
    api.get("/api/consents/me/status", { params: deviceId ? { device_id: deviceId } : {} }),
};

export const masterAdminApi = {
  overview: () => api.get("/api/master-admin/overview"),
  internalReadiness: () => api.get("/api/admin/internal-readiness"),
  users: (params = {}) => api.get("/api/master-admin/users", { params }),
  userDetail: (userId) => api.get(`/api/master-admin/users/${userId}`),

  approveUser: (userId, payload = {}) =>
    api.post(`/api/master-admin/users/${userId}/approve`, payload),

  deleteUser: (userId, payload = {}) =>
    api.post(`/api/master-admin/users/${userId}/delete`, payload),

  freezeUser: (userId, payload = {}) =>
    api.post(`/api/master-admin/users/${userId}/freeze`, payload),

  reactivateUser: (userId, payload = {}) =>
    api.post(`/api/master-admin/users/${userId}/reactivate`, payload),

  organizations: (params = {}) => api.get("/api/master-admin/organizations", { params }),
  organizationDetail: (organizationId) =>
    api.get(`/api/master-admin/organizations/${organizationId}`),
  updateOrganizationSeatPolicy: (organizationId, payload) =>
    api.post(`/api/master-admin/organizations/${organizationId}/seat-policy`, payload),
  // UPLOAD-CAP-1: set or CLEAR an org's upload cap. payload: { upload_cap, reason }.
  // upload_cap null (or omitted, or <= 0) clears it -> unlimited. Always audited server-side.
  updateOrganizationUploadCap: (organizationId, payload) =>
    api.post(`/api/master-admin/organizations/${organizationId}/upload-cap`, payload),
  // C-220 / C-163 §1: the org's privacy region, the scope source for UK-CONSENT-1.
  // payload: { region, reason? }. region is "GB" | "IL" | "US", or null to clear back to
  // "not set" -- which is a REAL value and the fail-closed state (C-163 §2), not a gap.
  setOrganizationPrivacyRegion: (organizationId, payload) =>
    api.post(`/api/master-admin/organizations/${organizationId}/privacy-region`, payload),
  // P5: the per-org Mira (reflective_pilot) enablement toggle. payload: { enabled, reason? }.
  // MA-AUDIT-1: one organisation's audit history, and its export. params: { start, end,
  // event_types, limit, cursor } -- start/end are inclusive UTC dates (YYYY-MM-DD).
  // The server records audit.viewed / audit.exported BEFORE it reads, and answers 503 with no
  // rows if it could not record the access. A 503 here is a REFUSAL, not an outage.
  getOrganizationAudit: (organizationId, params = {}) =>
    api.get(`/api/master-admin/organizations/${organizationId}/audit`, { params }),
  exportOrganizationAudit: (organizationId, params = {}) =>
    api.get(`/api/master-admin/organizations/${organizationId}/audit/export`, { params }),
  // W66: the Usage section. params: { period: "day"|"week"|"month", date, organization_id, role }.
  // Every number arrives as a cell { value, suppressed, no_data } -- k=3 is applied server-side.
  usageSummary: (params = {}) => api.get("/api/master-admin/usage/summary", { params }),
  // The CSV is built server-side from the same summary, so it is exactly what is on screen.
  usageExport: (params = {}) =>
    api.get("/api/master-admin/usage/export", { params, responseType: "blob" }),
  setReflectivePilot: (organizationId, payload) =>
    api.post(`/api/master-admin/organizations/${organizationId}/reflective-pilot`, payload),
  // CONSENT-UI-1 — grant or revoke one organization's student-voice consent. `reason` is sent on
  // BOTH grant and revoke because it lands in the master-admin audit entry for the change.
  setStudentVoiceConsent: (organizationId, payload) =>
    api.post(`/api/master-admin/organizations/${organizationId}/student-voice-consent`, payload),
  workspaces: (params = {}) => api.get("/api/master-admin/workspaces", { params }),
  workspaceDetail: (ownerUserId) => api.get(`/api/master-admin/workspaces/${ownerUserId}`),
  authEvents: (params = {}) => api.get("/api/master-admin/auth-events", { params }),
  auditEvents: (params = {}) => api.get("/api/master-admin/audit-events", { params }),
  incidents: (params = {}) => api.get("/api/master-admin/incidents", { params }),
  videos: (params = {}) => api.get("/api/master-admin/videos", { params }),
  videoDetail: (videoId) => api.get(`/api/master-admin/videos/${videoId}`),
  retryVideoAnalysis: (videoId) =>
    api.post(`/api/master-admin/videos/${videoId}/retry-analysis`),
  retryVideoPrivacy: (videoId) =>
    api.post(`/api/master-admin/videos/${videoId}/retry-privacy`),
  retryVideoTranscode: (videoId) =>
    api.post(`/api/master-admin/videos/${videoId}/retry-transcode`),
  storage: () => api.get("/api/master-admin/storage"),
  dependencies: () => api.get("/api/master-admin/dependencies"),
  aiQualityLatest: () => api.get("/api/admin/ai-quality/latest"),
  aiQualityHistory: () => api.get("/api/admin/ai-quality/history"),
  support: (params = {}) => api.get("/api/master-admin/support", { params }),
  revokeSessions: (userId, payload = {}) =>
    api.post(`/api/master-admin/users/${userId}/sessions/revoke`, payload),
  // priv-2-fix3: clear a user's two-factor state — lockout recovery when they lose their authenticator
  // AND their recovery codes. Super-admin only; audited (mfa_reset_by_admin).
  resetUserMfa: (userId, payload = {}) =>
    api.post(`/api/master-admin/users/${userId}/reset-mfa`, payload),
  // MA-UNLOCK-1 — a sibling read, not a field on userDetail: MasterAdminUserRecord lives in
  // server.py and is response_model-filtered, so a field added there would be stripped on the
  // wire and widening it means editing server.py, which the goal forbids.
  userLockout: (userId) => api.get(`/api/master-admin/users/${userId}/lockout`),
  unlockUser: (userId, payload = {}) =>
    api.post(`/api/master-admin/users/${userId}/unlock`, payload),
  exportDiagnosticBundle: (payload) =>
    api.post("/api/master-admin/diagnostic-bundles/export", payload),
};

export const opsApi = {
  readiness: () => api.get("/api/admin/ops/readiness"),
  launchHealth: () => api.get("/api/admin/ops/launch-health"),
  observability: () => api.get("/api/admin/ops/observability"),
  aiQuality: () => api.get("/api/admin/ops/ai-quality"),
  backlogPriorities: () => api.get("/api/admin/ops/backlog-priorities"),
};

export default api;
